/**
 * /crm/analytics/sla — SLA disposition analytics dashboard.
 *
 * Pulls aggregates from `/api/bi/crm/analytics/sla-disposition/` so
 * management can spot which discipline / store / cause is generating
 * the most breaches, and how many of them the agent flagged as
 * avoidable. Charts intentionally stay simple (counts + horizontal
 * bars) so the page renders fast even for a 30-day window.
 */
import { useMemo, useState } from 'react';
import { Loader2, RefreshCcw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useSlaDispositionAggregate } from '@/api/bi/crm';
import { cn } from '@/lib/cn';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const DEFAULT_END = new Date();
const DEFAULT_START = new Date(DEFAULT_END.getTime() - 14 * 24 * 60 * 60 * 1000);

export function CrmSlaAnalyticsPage() {
  const [start, setStart] = useState<string>(isoDate(DEFAULT_START));
  const [end, setEnd] = useState<string>(isoDate(DEFAULT_END));
  const [avoidable, setAvoidable] = useState<'all' | 'yes' | 'no' | 'unknown'>('all');

  const query = useSlaDispositionAggregate({
    start: start ? new Date(`${start}T00:00:00`).toISOString() : null,
    end: end ? new Date(`${end}T23:59:59`).toISOString() : null,
    isAvoidable: avoidable === 'all' ? null : avoidable,
  });

  const totals = query.data?.total_tickets ?? 0;
  const avoidableCount = query.data?.by_avoidable?.['yes'] ?? 0;
  const unavoidableCount = query.data?.by_avoidable?.['no'] ?? 0;
  const unknownCount =
    (query.data?.by_avoidable?.['unknown'] ?? 0) + (query.data?.by_avoidable?.['unset'] ?? 0);

  const maxCategoryCount = useMemo(() => {
    const rows = query.data?.by_category ?? [];
    return rows.reduce((m, r) => Math.max(m, r.count), 1);
  }, [query.data]);

  const maxReasonCount = useMemo(() => {
    const rows = query.data?.by_reason ?? [];
    return rows.reduce((m, r) => Math.max(m, r.count), 1);
  }, [query.data]);

  const maxStoreCount = useMemo(() => {
    const rows = query.data?.by_store ?? [];
    return rows.reduce((m, r) => Math.max(m, r.count), 1);
  }, [query.data]);

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="SLA Disposition Analytics"
        description="Where breaches are concentrating — by category, reason, sub-reason, store, and avoidability."
        actions={
          <Button size="sm" variant="outline" onClick={() => void query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1 h-3.5 w-3.5" />}
            Refresh
          </Button>
        }
      />

      <Card className="mb-4">
        <CardBody className="grid gap-3 md:grid-cols-4">
          <Field label="From">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
          <Field label="Avoidable filter">
            <Select value={avoidable} onValueChange={(v) => setAvoidable(v as typeof avoidable)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Avoidable only</SelectItem>
                <SelectItem value="no">Unavoidable only</SelectItem>
                <SelectItem value="unknown">Unknown only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-end gap-2">
            <Badge tone="primary">{totals} tickets</Badge>
            <Badge tone="warning">{avoidableCount} avoidable</Badge>
            <Badge tone="success">{unavoidableCount} unavoidable</Badge>
            <Badge tone="neutral">{unknownCount} unknown</Badge>
          </div>
        </CardBody>
      </Card>

      {query.isLoading ? (
        <Skeleton className="h-96" />
      ) : query.isError ? (
        <ErrorState
          title="Could not load SLA analytics"
          description={String((query.error as Error)?.message ?? '')}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>By category (root)</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.by_category ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tickets in range.</p>
              ) : (
                <ul className="space-y-1.5">
                  {query.data!.by_category.map((row) => (
                    <BarRow
                      key={row.root_id}
                      label={row.root_label}
                      sub={row.root_code}
                      count={row.count}
                      max={maxCategoryCount}
                    />
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By reason</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.by_reason ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tickets in range.</p>
              ) : (
                <ul className="space-y-1.5">
                  {query.data!.by_reason.slice(0, 12).map((row) => (
                    <BarRow
                      key={row.reason_id}
                      label={row.reason_label}
                      sub={row.root_label ?? ''}
                      count={row.count}
                      max={maxReasonCount}
                    />
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By sub-reason</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.by_sub_reason ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tickets in range.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase tracking-[0.12em] text-muted-foreground">
                    <tr>
                      <th className="py-1">Reason</th>
                      <th className="py-1">Sub-reason</th>
                      <th className="py-1 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data!.by_sub_reason.slice(0, 20).map((r) => (
                      <tr key={r.sub_reason_id} className="border-t border-border/60">
                        <td className="py-1.5 text-muted-foreground">{r.reason_label ?? '—'}</td>
                        <td className="py-1.5 font-medium">{r.sub_reason_label}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By store</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.by_store ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tickets in range.</p>
              ) : (
                <ul className="space-y-1.5">
                  {query.data!.by_store.slice(0, 15).map((row) => (
                    <BarRow
                      key={row.store_id}
                      label={row.store_name}
                      sub={`${row.avoidable} avoidable`}
                      count={row.count}
                      max={maxStoreCount}
                    />
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By breach tag</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.by_breach_tag ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tickets in range.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase tracking-[0.12em] text-muted-foreground">
                    <tr>
                      <th className="py-1">Tag</th>
                      <th className="py-1 text-right">Count</th>
                      <th className="py-1 text-right">Avg lateness (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data!.by_breach_tag.map((r) => (
                      <tr key={r.tag} className="border-t border-border/60">
                        <td className="py-1.5 font-medium">{r.tag}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.count}</td>
                        <td className="py-1.5 text-right tabular-nums">
                          {Math.round(r.avg_late_seconds / 60)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Daily trend</CardTitle>
            </CardHeader>
            <CardBody>
              {(query.data?.trend ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No data in range.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase tracking-[0.12em] text-muted-foreground">
                    <tr>
                      <th className="py-1">Day</th>
                      <th className="py-1 text-right">Total</th>
                      <th className="py-1 text-right">Avoidable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data!.trend.map((r) => (
                      <tr key={r.day ?? ''} className="border-t border-border/60">
                        <td className="py-1.5">{r.day ?? '—'}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.count}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.avoidable}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}

function BarRow({
  label,
  sub,
  count,
  max,
}: {
  label: string;
  sub: string;
  count: number;
  max: number;
}) {
  const pct = Math.max(2, Math.round((count / Math.max(1, max)) * 100));
  return (
    <li className="flex items-center gap-3">
      <div className="min-w-[160px] flex-1">
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="font-medium truncate">{label}</span>
          <span className="tabular-nums text-muted-foreground">{count}</span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-muted/40">
          <div
            className={cn('h-1.5 rounded-full bg-primary')}
            style={{ width: `${pct}%` }}
          />
        </div>
        {sub ? <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p> : null}
      </div>
    </li>
  );
}

